/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { CheckboxToggleMixin } from '../../utils/checkbox-toggle-mixin.js';
import { ControlStateMixin } from '../../utils/control-state-mixin.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { FocusBlurMixin } from '../../utils/focus-blur-mixin.js';
import { extendInteractiveArea } from '../../utils/interactive-area.js';
import toggleStyles from './sh-toggle.lit.scss.js';

/**
 * @prop {String} label - Default label. Defines the label of the Toggle.
 * @prop {Boolean} active - Default false. Defines if a Toggle is selected or not. Changes when clicked.
 * @prop {Boolean} disabled - Default false. Defines if a Toggle is disabled. If true, it won't allow clicks.
 * @prop {Boolean} neutral - Default false. If true, the active state will be gray instead of orange.
 */

export class SHToggle extends CheckboxToggleMixin(ControlStateMixin(FocusBlurMixin(LitElement))) {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      disabled: { type: Boolean, reflect: true },
    };
  }

  static get styles() {
    return [sharedStyles, toggleStyles, extendInteractiveArea()];
  }

  render() {
    return html`
      <div class="toggle focus-item"></div>
      <span class="toggle-label">${this.label}</span>
    `;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    const hasValue = newValue !== null;
    switch (name) {
      case 'active':
        this.setAttribute('aria-checked', hasValue);
        break;
    }
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    dispatchAttributeChangedEvent(this, changedProperties, ['label']);
  }

  constructor() {
    super();
    this.label = 'label';
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'switch');
    }
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
  }

  firstUpdated() {
    super.firstUpdated();
  }

  _spaceKeyAction() {
    super._spaceKeyAction();
    this.handleClick();
  }
}

customElements.define('sh-toggle', SHToggle);
